import { Shape } from '@/services/shape';
import { getImageSize } from '@/utils/fs/image.utils';
import { Modal } from 'ant-design-vue';
import { dualtimeCityList } from '@/resources/data-source';
import { DataSourceFormat } from '@/resources/enum';
import { WatchfaceDeviceType } from './use-watchface-devices';
import { canUse } from '@/utils/can-use.utils';

export enum CompareImagesSizeResult {
  Equal,
  NotEqualSelected,
  NotEqualUploaded
}

export interface ImageArrayIndex {
  index: number;
  label: string;
  labelSub?: string;
  required?: boolean;
  // 空状态类型：empty = 空，negative = 负号
  emptyImageType?: 'empty' | 'negative';
  // 是否为隐藏的占位项（UI上不可见，但占据数组位置）
  hidden?: boolean;
}

export const isN65ABWeatherPrecision = (
  sourceItem: DataSourceItem,
  deviceType: string
) => {
  // 如果是N65AB，且是温度时，需要放开小数点
  return (
    ['N65A', 'N65B', 'N65C'].includes(deviceType ?? '') &&
    [
      'weatherCurrentTemperatureFahrenheit',
      'weatherTodayTemperatureMaxFahrenheit',
      'weatherTodayTemperatureMinFahrenheit',
      'weatherTomorrowTemperatureMaxFahrenheit',
      'weatherTomorrowTemperatureMinFahrenheit'
    ].includes(sourceItem?.key ?? '')
  );
};

// 获取指南针方向
export const getCompassDirectionsArr = (
  useInAst = false,
  needEmptyValue = false
) => {
  const res = [
    {
      value: 0,
      label: '北',
      desc: '337° <= 指南针角度 < 22°'
    },
    {
      value: 22,
      label: '东北',
      desc: '22° <= 指南针角度 < 67°'
    },
    {
      value: 67,
      label: '东',
      desc: '67° <= 指南针角度 < 112°'
    },
    {
      value: 112,
      label: '东南',
      desc: '112° <= 指南针角度 < 157°'
    },
    {
      value: 157,
      label: '南',
      desc: '157° <= 指南针角度 < 202°'
    },
    {
      value: 202,
      label: '西南',
      desc: '202° <= 指南针角度 < 247°'
    },
    {
      value: 247,
      label: '西',
      desc: '247° <= 指南针角度 < 292°'
    },
    {
      value: 292,
      label: '西北',
      desc: '292° <= 指南针角度 < 337°'
    },
    {
      value: 'INT_MAX',
      label: '未知'
    },
    {
      value: 337,
      label: '北'
    }
  ];
  if (!useInAst) {
    // 上传图片时不需要上传最后一张“北”
    res.pop();
    // 如果不需要空状态，去掉空状态子元素
    if (!needEmptyValue) res.pop();
  } else {
    res.splice(res.length - 2, 1);
  }
  return res;
};

export const getImageArrayIndexs = (
  shape: Shape,
  deviceType: WatchfaceDeviceType
) => {
  const {
    fillType = '普通填充',
    sourceProgressAccuracy = 10,
    sourceWeatherType = 'whole',
    sourceHourMode = 'system',
    sourceProgressCustom
  } = shape.attrs;
  const {
    labelRange,
    progressAccuracy = [],
    maxValue = 0,
    weatherValueRange,
    canNotReachMaxValue: _canNotReachMaxValue
  } = shape.sourceItem ?? {};
  const res: ImageArrayIndex[] = [];

  if (shape.sourceItems.filter((item) => !!item).length < 1) return res;
  let canNotReachMaxValue = _canNotReachMaxValue;
  let min = Math.min(...shape.sourceItems.map((item) => item?.minValue ?? 0));
  let max = Math.max(...shape.sourceItems.map((item) => item?.maxValue ?? 0));

  // 是否存在无效值
  const isSupportEmpty = canUse('shape-image-array-empty', deviceType).valid;
  const needEmptyValue = shape.sourceItem?.needEmptyValue
    ? shape.sourceItem?.needEmptyValue(deviceType, fillType)
    : false;
  const hasInvalidValueImage = isSupportEmpty && needEmptyValue;

  if (fillType == '数字填充') {
    // 图组中图片顺序必须为"0~9", "-", "."
    const count = Math.max(Math.min(9, max), 0);
    // 需要0-9 - . 数字
    // 无论数据源是否从0开始，数组都从0开始
    for (let i = 0; i <= count; i++) {
      res.push({
        index: i,
        label: String(i)
      });
    }
    // 是否需要包含小数点
    const includePrecision = shape.sourceItems.some((item) => {
      if (item && isN65ABWeatherPrecision(item, deviceType)) return true;
      return !!item?.precision;
    });
    
    // 添加 - 作为符号或无效值
    // 1. 当数据源需要添加无效值值
    // 2. 数据源有负数时
    let nextIndex = count + 1;
    if (hasInvalidValueImage || min < 0) {
      res.push({
        index: nextIndex,
        label: min < 0 ? '负号' : '空状态',
        emptyImageType: min < 0 ? 'negative' : 'empty'
      });
      nextIndex++;
    } else if (includePrecision) {
      // 没有负号也没有空状态，但有小数点时，固件端仍需要 index 10 位置有图片
      // 添加隐藏占位项，上传第一张图片时会自动填充到此位置
      res.push({
        index: nextIndex,
        label: '占位',
        hidden: true
      });
      nextIndex++;
    }

    // 是否需要添加小数点
    if (includePrecision) {
      res.push({
        index: nextIndex,
        label: '小数点'
      });
    }
    return res;
  } else {
    // 普通填充
    const _addEmptyIfNeed = (emptyIndex: number) => {
      if (hasInvalidValueImage) {
        res.push({
          index: emptyIndex,
          label: '空状态',
          emptyImageType: 'empty'
        });
      }
    };
    // 普通填充
    // 天气数据源
    if (weatherValueRange?.[sourceWeatherType]?.length) {
      weatherValueRange?.[sourceWeatherType]?.forEach((item, index) => {
        res.push({
          index,
          label: item.label,
          required: item.required
        });
      });

      return res;
    } else if (progressAccuracy.length) {
      if (sourceProgressAccuracy) {
        // 进度类数据源
        let start = 0;
        let index = 0;
        if (maxValue != 100) throw '最大值不是100，无法使用进度格式';
        while (start <= maxValue) {
          res.push({
            index,
            label: start + '%'
          });
          start += sourceProgressAccuracy;
          index++;
        }
      } else {
        sourceProgressCustom?.forEach((item, index) => {
          res.push({
            index,
            label: index + '%'
          });
        });
      }

      _addEmptyIfNeed(res.length);
      return res;
    } else if (shape.attrs.source === 'dualtimeCity') {
      // 双时区城市列表
      dualtimeCityList.forEach((item, index) => {
        res.push({ index, label: item.name, labelSub: item.brief });
      });
      return res;
    } else if (shape.attrs.source === 'systemSensorCompassDirection') {
      // 指南针
      getCompassDirectionsArr(false, needEmptyValue).forEach((item, index) => {
        res.push({ index, label: item.label });
      });
      return res;
    } else {
      // 时间数据源，需要考虑是否12小时制
      if (shape.attrs.source == 'timeHour') {
        if (sourceHourMode == '12h') {
          max = 12;
          min = 1;
          canNotReachMaxValue = false;
        }
      }

      // 其他普通填充，从min一直遍历到最大值
      const count = max - min - (canNotReachMaxValue ? 1 : 0);
      // 无论数据源是否从0开始，数组都从0开始
      for (let i = 0; i <= count; i++) {
        res.push({
          index: i,
          label: labelRange?.[i] ?? i + min + ''
        });
      }
      // 如果存在无效值，需要放在数组最后
      _addEmptyIfNeed(count + 1);
      return res;
    }
  }
};

export const getWidgetArrayUnitIconIndexs = (shape: Shape) => {
  const res: {
    index: number;
    label: string;
    resourceName: 'unitIcon' | 'unitIcon2';
  }[] = [];
  const widgetImageArray = shape.widgetImageArray;
  if (widgetImageArray == 'time') {
    res.push({ index: 0, label: ':', resourceName: 'unitIcon' });
  }
  if (widgetImageArray == 'date') {
    const sourceFormat = shape.attrs.sourceFormat ?? 1;
    if (
      [
        DataSourceFormat['Format_08/16'],
        DataSourceFormat['Format_8/16']
      ].includes(sourceFormat)
    ) {
      res.push({ index: 0, label: '/', resourceName: 'unitIcon' });
    }
    if (
      [
        DataSourceFormat['Format_08月16日'],
        DataSourceFormat['Format_8月16日']
      ].includes(sourceFormat ?? 1)
    ) {
      res.push(
        { index: 0, label: '月', resourceName: 'unitIcon' },
        { index: 1, label: '日', resourceName: 'unitIcon2' }
      );
    }
  }
  return res;
};

export const useImageArray = () => {
  /**
   * 判断所选图片尺寸是否一致、与已上传图片是否一致
   * @param selected 所选图片, 字符串值为本地路径, 长度需等于图组资源数量
   * @param shape 当前shape, 用于获取已上传图片尺寸
   * @param colorId 配色id
   * @returns enum CompareSizeResult
   */
  const compareImagesSize = async (
    selected: string[],
    shape: Shape,
    colorId: string
  ) => {
    // 传入空数组 不通过
    if (!selected.length) return CompareImagesSizeResult.NotEqualSelected;
    const selectedSizes = (
      await Promise.all(
        selected.map((i) => (i.length ? getImageSize(i) : undefined))
      )
    ).filter((i) => i);
    // 无有效尺寸 不通过
    if (!selectedSizes.length) return CompareImagesSizeResult.NotEqualSelected;
    // 校验所选图片尺寸是否一致
    if (
      selectedSizes.some(
        (i) =>
          i?.width !== selectedSizes[0]?.width ||
          i?.height !== selectedSizes[0]?.height
      )
    )
      return CompareImagesSizeResult.NotEqualSelected;
    // 取第一个有效的已上传图片
    try {
      const firstValidSize = await shape.getImageSize('images', colorId);
      if (!firstValidSize) return CompareImagesSizeResult.Equal;
      if (
        firstValidSize.width !== selectedSizes[0]?.width ||
        firstValidSize.height !== selectedSizes[0]?.height
      )
        return CompareImagesSizeResult.NotEqualUploaded;
      return CompareImagesSizeResult.Equal;
    } catch (e) {
      // 当前未上传图片 直接通过
      return CompareImagesSizeResult.Equal;
    }
  };
  /**
   * 判断所选图片尺寸是否一致、与已上传图片是否一致, 不合法时会阻塞Promise, 不运行后续逻辑
   * @param selected 所选图片, 字符串值为本地路径, 长度需等于图组资源数量
   * @param shape 当前shape, 用于获取已上传图片尺寸
   * @param colorId 配色id
   * @param allowDeleteAll 是否允许用户删除已上传图片
   * @returns Promise
   */
  const isSameSizeBetweenImages = async (
    selected: string[],
    shape: Shape,
    colorId: string,
    allowDeleteAll = false
  ) => {
    const comp = await compareImagesSize(selected, shape, colorId);
    await new Promise<void>((res) => {
      if (comp === CompareImagesSizeResult.Equal) res();
      else if (comp === CompareImagesSizeResult.NotEqualSelected)
        Modal.info({
          title: '上传失败',
          content: '图片宽高不一致，请重新上传。',
          okText: '知道了',
          okType: 'default',
          autoFocusButton: null
        });
      else if (comp === CompareImagesSizeResult.NotEqualUploaded)
        allowDeleteAll
          ? Modal.confirm({
              title: '上传失败',
              content: '与已上传图片宽高不一致，请重新上传。',
              okText: '删除原图片',
              okType: 'danger',
              cancelText: '关闭',
              autoFocusButton: null,
              onOk: () => {
                shape.deleteResource({
                  colorGroup: colorId
                });
                res();
              }
            })
          : Modal.info({
              title: '上传失败',
              content: '与已上传图片宽高不一致，请重新上传。',
              okText: '知道了',
              okType: 'default',
              autoFocusButton: null
            });
    });
  };
  return { compareImagesSize, isSameSizeBetweenImages };
};
